import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { logEvent } from "@/hooks/use-event-log";
import { useAuth } from "@/contexts/AuthContext";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { normalizePhoneE164 } from "@/lib/phone-format";
import type { DBCustomerContact } from "@/lib/db-types";

// Listen er bundet av customer_id og er liten per kunde, så plain select uten
// paginering er trygt (scripts/paginate-gate.mjs dekker ikke tabellen).

/** Aktive først, hovedkontakt øverst, anonymiserte nederst. */
export function useCustomerContacts(customerId: string | undefined) {
  return useQuery({
    queryKey: ["customer-contacts", customerId],
    queryFn: async (): Promise<DBCustomerContact[]> => {
      const { data, error } = await supabase
        .from("customer_contacts")
        .select("*")
        .eq("customer_id", customerId!)
        .order("is_primary", { ascending: false })
        .order("full_name", { ascending: true });
      if (error) throw error;
      const rows = (data ?? []) as DBCustomerContact[];
      const rank = (c: DBCustomerContact) => (c.anonymized_at ? 2 : c.is_active ? 0 : 1);
      return [...rows].sort((a, b) => rank(a) - rank(b));
    },
    enabled: !!customerId,
    staleTime: 60 * 1000,
  });
}

export interface ContactInput {
  full_name: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

/** Tom streng blir null; ugyldig mobilnummer avvises i stedet for å lagres. */
export function cleanContactFields(input: Partial<ContactInput>): Partial<ContactInput> {
  const out: Partial<ContactInput> = {};
  if (input.full_name !== undefined) {
    const name = input.full_name.trim();
    if (!name) throw new Error("Navn mangler");
    out.full_name = name;
  }
  for (const key of ["title", "email", "notes"] as const) {
    if (input[key] !== undefined) out[key] = input[key]?.trim() || null;
  }
  if (input.phone !== undefined) {
    const raw = input.phone?.trim() ?? "";
    if (!raw) out.phone = null;
    else {
      const e164 = normalizePhoneE164(raw);
      if (!e164) throw new Error("Ugyldig mobilnummer");
      out.phone = e164;
    }
  }
  return out;
}

type ContactEventType =
  | "crm_contact_created"
  | "crm_contact_updated"
  | "crm_contact_deactivated"
  | "crm_contact_confirmed"
  | "crm_contact_anonymized";

/**
 * Felles audit + invalidering for alle kontaktmutasjoner. Aktør er ALLTID den
 * faktisk innloggede brukeren (useAuth); ved impersonering ligger den
 * impersonerte i payload.on_behalf_of. Payload har aldri e-post/telefon.
 */
function useContactAudit() {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const { activeUser, isImpersonating } = useImpersonation();
  return {
    authUserId: authUser?.id ?? null,
    async finish(eventType: ContactEventType, contact: Pick<DBCustomerContact, "id" | "customer_id" | "full_name">, anonymized = false) {
      if (authUser?.id) {
        logEvent({
          event_type: eventType,
          entity_type: "customer_contacts",
          entity_id: contact.id,
          user_id: authUser.id,
          payload: {
            customer_id: contact.customer_id,
            contact_id: contact.id,
            ...(anonymized ? {} : { full_name: contact.full_name }),
            ...(isImpersonating && activeUser ? { on_behalf_of: activeUser.id } : {}),
          },
        });
      }
      await invalidateFor(qc, "customer_contacts", { customerId: contact.customer_id });
    },
  };
}

const onError = (prefix: string) => (err: Error) => toast.error(`${prefix}: ${err.message}`);

export function useCreateCustomerContact() {
  const audit = useContactAudit();
  return useMutation({
    mutationFn: async (input: ContactInput & { customer_id: string; source?: DBCustomerContact["source"] }) => {
      const fields = cleanContactFields(input);
      const { data, error } = await supabase
        .from("customer_contacts")
        .insert({
          customer_id: input.customer_id,
          source: input.source ?? "manual",
          ...fields,
          created_by: audit.authUserId,
        })
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerContact;
    },
    onSuccess: (row) => audit.finish("crm_contact_created", row),
    onError: onError("Kunne ikke opprette kontaktperson"),
  });
}

export function useUpdateCustomerContact() {
  const audit = useContactAudit();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<ContactInput> & { id: string }) => {
      const fields = cleanContactFields(patch);
      const { data, error } = await supabase
        .from("customer_contacts")
        .update(fields)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerContact;
    },
    onSuccess: (row) => audit.finish("crm_contact_updated", row),
    onError: onError("Kunne ikke lagre kontaktperson"),
  });
}

/** Bytter hovedkontakt atomisk via RPC (den partielle unike indeksen tåler ikke to samtidige). */
export function useSetPrimaryContact() {
  const audit = useContactAudit();
  return useMutation({
    mutationFn: async (contact: Pick<DBCustomerContact, "id" | "customer_id" | "full_name">) => {
      const { error } = await supabase.rpc("set_primary_customer_contact", { p_contact_id: contact.id });
      if (error) throw error;
      return contact;
    },
    onSuccess: (contact) => audit.finish("crm_contact_updated", contact),
    onError: onError("Kunne ikke sette hovedkontakt"),
  });
}

export function useSetContactInactive() {
  const audit = useContactAudit();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string | null }) => {
      const { data, error } = await supabase
        .from("customer_contacts")
        .update({ is_active: false, is_primary: false, inactive_reason: reason?.trim() || null })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerContact;
    },
    onSuccess: (row) => audit.finish("crm_contact_deactivated", row),
    onError: onError("Kunne ikke sette kontaktperson inaktiv"),
  });
}

/** Bekrefter en person agenten har foreslått. */
export function useConfirmAgentContact() {
  const audit = useContactAudit();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("customer_contacts")
        .update({ confirmed_at: new Date().toISOString(), confirmed_by: audit.authUserId })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerContact;
    },
    onSuccess: (row) => audit.finish("crm_contact_confirmed", row),
    onError: onError("Kunne ikke bekrefte kontaktperson"),
  });
}

/**
 * «Slett person» = anonymisering (GDPR). Raden beholdes så kontaktloggen
 * fortsatt peker et sted; navn, e-post, mobil, tittel og notater fjernes.
 * DB-triggeren guard_contact_anonymized låser raden etterpå.
 */
export function useAnonymizeContact() {
  const audit = useContactAudit();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from("customer_contacts")
        .update({
          full_name: "Slettet person",
          email: null,
          phone: null,
          title: null,
          notes: null,
          inactive_reason: null,
          is_active: false,
          is_primary: false,
          anonymized_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data as DBCustomerContact;
    },
    onSuccess: (row) => audit.finish("crm_contact_anonymized", row, true),
    onError: onError("Kunne ikke slette personen"),
  });
}
